/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  collection,
  doc,
  getDocs,
  increment,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { toast } from "react-hot-toast";

import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { db } from "../config/firebaseConfig";
import { UserAuth } from "../context/authContext";
import CopyComponent from "./utils/CopyComponent";
import FirestoreTimestampToDate from "./utils/FirebaseTimetoDate";
import { calculateHash } from "./utils/helper";

export default function BlocksTable() {
  const [roomId, setId] = useState(localStorage.getItem("roomId") ?? "");
  const [selectedBlock, setSelectedBlock] = useState();

  const [blocks, setBlocks] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("hash");
  const { user } = UserAuth();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const fetchRoomTransactions = async () => {
    if (!roomId) return;

    try {
      const roomRef = doc(db, "rooms", roomId);

      let tempBlocks = [];

      const blocksRes = await getDocs(
        query(collection(roomRef, "blocks"), orderBy("timestamp", "asc")),
      );

      tempBlocks = [
        ...tempBlocks,
        ...blocksRes.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      ];

      setBlocks(tempBlocks);

      const transactionsRes = await getDocs(
        query(
          collection(roomRef, "transactions"),
          where("status", "==", "Added to block"),
          orderBy("addedAt", "asc"),
        ),
      );

      const transactions = transactionsRes.docs.reduce(
        (result, value, index, array) => {
          if (index % 5 === 0)
            result.push({
              transactions: array
                .slice(index, index + 5)
                .map((v) => ({ id: v.id, ...v.data() })),
            });
          return result;
        },
        [],
      );

      setPendingTransactions(transactions);
    } catch (error) {
      console.error("Error fetching room transactions:", error);
      toast.error("Error fetching room transactions.");
    }
  };

  async function saveBlock(
    calculatedHash,
    timestamp,
    previousBlockHash,
    transactions,
    nonce,
  ) {
    try {
      const reward = transactions.reduce((acc, curr) => {
        return acc + curr.amount * 0.1;
      }, 0);
      const receivers = Object.fromEntries(
        (
          await getDocs(
            query(
              collection(db, "users"),
              where(
                "publicKey",
                "in",
                transactions.map((t) => t.receiver),
              ),
            ),
          )
        ).docs.map((user) => [
          user.data().publicKey,
          { id: user.id, ...user.data() },
        ]),
      );

      await runTransaction(db, async (dbTransaction) => {
        const res = dbTransaction.set(
          doc(db, "rooms", roomId, "blocks", calculatedHash),
          {
            timestamp,
            previousBlockHash,
            hash: calculatedHash,
            transactions,
            nonce,
            created_at: serverTimestamp(),
          },
        );

        // Reward for miner
        dbTransaction.update(doc(db, "users", user.email), {
          balance: increment(reward),
        });

        transactions.forEach((t) => {
          // Send trnasaction amount
          receivers.hasOwnProperty(t.receiver) &&
            dbTransaction.update(
              doc(db, "users", receivers[t.receiver].email),
              {
                balance: increment(t.amount * 0.9),
              },
            );

          dbTransaction.update(doc(db, "rooms", roomId, "transactions", t.id), {
            status: "Added to chain",
          });
        });
      });

      toast.success("Added block to blockchain");
      window.location.href = "/joinedroom";
      fetchRoomTransactions();
    } catch (e) {
      console.error("Failed to save block", e);
      toast.error("Failed to save block");
    }
  }

  useEffect(() => {
    if (roomId) fetchRoomTransactions();
  }, [roomId]);

  const renderTrimmedAddress = (address) => `${address.substring(0, 10)}...`;

  const copyToClipboard = (e, data) => {
    e.preventDefault(); // Prevent the default action associated with the event

    navigator.clipboard.writeText(data).then(
      () => {
        toast.success("Copied Successfully");
      },
      (err) => {
        toast.error("Failed to copy");
      },
    );
  };

  return (
    <div className="min-h-screen  p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-2xl font-bold text-white mb-6">
          Blocks for: <span className="text-[#6C4FFF]">{roomId}</span>
        </h2>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search..."
            className="flex-1 px-4 py-3 bg-[#343B4F] text-white rounded-lg border border-[#1B1E3E] focus:outline-none focus:border-[#6C4FFF]"
          />
          <select
            value={filter}
            onChange={handleFilterChange}
            className="px-4 py-3 bg-[#343B4F] text-white rounded-lg border border-[#1B1E3E] focus:outline-none focus:border-[#6C4FFF]"
          >
            <option value="hash">Transaction Hash</option>
            <option value="nonce">Nonce</option>
            <option value="sender">Sender</option>
            <option value="receiver">Receiver</option>
            <option value="signature">Signature</option>
            <option value="status">Status</option>
            <option value="timestamp">Timestamp</option>
            <option value="amount">Amount</option>
          </select>
        </div>

        {/* Blocks Table */}
        <div className="bg-[#343B4F] rounded-lg overflow-hidden mb-6 custom-scrollbar">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1B1E3E]">
                  <th className="text-left py-4 px-4 text-gray-300 font-semibold text-sm">
                    Block
                  </th>
                  <th className="text-left py-4 px-4 text-gray-300 font-semibold text-sm">
                    Transactions
                  </th>
                  <th className="text-left py-4 px-4 text-gray-300 font-semibold text-sm">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {blocks &&
                  [...blocks, ...pendingTransactions].map((block, index) => (
                    <tr
                      key={index}
                      className="border-b border-[#1B1E3E] hover:bg-[#2a3347] transition"
                    >
                      {!block.hash ? (
                        <>
                          <td className="py-4 px-4">
                            {block.hash ? (
                              <span className="text-white font-mono text-sm">
                                {block.hash}
                              </span>
                            ) : (
                              <span className="text-yellow-400 font-medium">
                                Pending to Mine
                              </span>
                            )}
                          </td>

                          <td className="py-4 px-4">
                            {block.status === "genesis" ? (
                              <span className="text-green-400 font-semibold">
                                Genesis
                              </span>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[600px]">
                                  <thead>
                                    <tr className="border-b border-[#1B1E3E]/50">
                                      <th className="text-left py-2 px-2 text-gray-400 text-xs">
                                        Transaction Hash
                                      </th>
                                      <th className="text-left py-2 px-2 text-gray-400 text-xs">
                                        Time Stamp
                                      </th>
                                      <th className="text-left py-2 px-2 text-gray-400 text-xs">
                                        Sender Address
                                      </th>
                                      <th className="text-left py-2 px-2 text-gray-400 text-xs">
                                        Receiver Address
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {block.transactions &&
                                      block.transactions.length > 0 &&
                                      block.transactions.map((t, idx) => (
                                        <tr
                                          key={idx}
                                          className="border-b border-[#1B1E3E]/30"
                                        >
                                          <td className="py-2 px-2">
                                            <CopyComponent data={t.hash} />
                                          </td>
                                          <td className="py-2 px-2 text-gray-300 text-xs">
                                            {t.addedAt &&
                                              FirestoreTimestampToDate(
                                                t.addedAt,
                                              )}
                                          </td>
                                          <td className="py-2 px-2">
                                            <CopyComponent data={t.sender} />
                                          </td>
                                          <td className="py-2 px-2">
                                            <CopyComponent data={t.receiver} />
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {block.status !== "genesis" && !block.id && (
                              <button
                                className="px-4 py-2 bg-gradient-to-r from-[#6C4FFF] to-[#5840D9] text-white rounded-lg hover:opacity-90 transition text-sm font-medium"
                                onClick={() => {
                                  setSelectedBlock({
                                    transactions:
                                      (block.transactions &&
                                        block.transactions) ||
                                      [],
                                    previousBlockHash: [
                                      ...blocks,
                                      ...pendingTransactions,
                                    ][index - 1].hash,
                                  });
                                  toast.success("Block selected for mining");
                                }}
                              >
                                Add Block
                              </button>
                            )}
                          </td>
                        </>
                      ) : (
                        ""
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mining Visualizer */}
        <div className="bg-[#343B4F] rounded-lg p-6">
          <h2 className="text-xl font-bold text-white text-center mb-6">
            Mining Visualizer
          </h2>
          <Visualizer
            transactions={(selectedBlock && selectedBlock.transactions) || []}
            previousBlockHash={
              (selectedBlock && selectedBlock.previousBlockHash) || undefined
            }
            save={saveBlock}
          />
        </div>
      </div>
    </div>
  );
}

function Visualizer({
  transactions = [],
  previousBlockHash,
  initialNonce = 0,
  timestamp = dayjs().unix(),
  difficulty = 2,
  save,
}) {
  const [nonce, setNonce] = useState(initialNonce ?? 0);
  const [calculatedHash, setCalculatedHash] = useState("");
  const [foundHash, setFoundHash] = useState(false);
  const [roomId, setRoomId] = useState(localStorage.getItem("roomId") ?? "");

  async function mine(
    hash = calculateHash(transactions, nonce, timestamp, previousBlockHash),
  ) {
    if (foundHash) return;

    const start = Date.now();

    let nonce = initialNonce;

    while (hash.slice(0, difficulty) !== "0".repeat(difficulty) && !foundHash) {
      const current = Date.now();

      hash = calculateHash(transactions, nonce, timestamp, previousBlockHash);

      setCalculatedHash(hash);

      const currentTime = Math.floor((current - start) / 1000);

      setNonce(++nonce);

      await new Promise((res) => setTimeout(res, 5));
    }

    setFoundHash(true);

    const end = Date.now();
    const time = (end - start) / 1000;
  }

  return (
    <div>
      {transactions && transactions.length < 1 && (
        <div className="text-center py-8">
          <p className="text-gray-400 text-lg">Select Transactions to Mine</p>
        </div>
      )}
      {transactions && transactions.length > 0 && (
        <div className="bg-[#2a3347] rounded-lg p-6 max-w-2xl mx-auto">
          <div className="border-b border-[#1B1E3E] pb-4 mb-4">
            <h3 className="text-white text-lg font-semibold text-center">
              Block Miner
            </h3>
          </div>

          <div className="space-y-4">
            {/* Nonce */}
            <div>
              <h4 className="text-gray-300 font-medium mb-2">Nonce:</h4>
              <input
                className="w-full px-4 py-3 bg-[#343B4F] text-white rounded-lg border border-[#1B1E3E] font-mono"
                value={nonce}
                readOnly
              />
            </div>

            {/* Calculated Hash */}
            <div>
              <h4 className="text-gray-300 font-medium mb-2">
                Calculated Hash:
              </h4>
              <input
                type="text"
                value={calculatedHash}
                className="w-full px-4 py-3 bg-[#343B4F] text-[#6C4FFF] rounded-lg border border-[#1B1E3E] font-mono text-sm"
                readOnly
              />
            </div>

            {/* Found Hash */}
            <div>
              <h4 className="text-gray-300 font-medium mb-2">Found Hash:</h4>
              <input
                type="text"
                className="w-full px-4 py-3 bg-[#343B4F] text-white rounded-lg border border-[#1B1E3E] font-mono"
                value={foundHash ? "true" : "false"}
                readOnly
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 justify-center mt-6">
            <button
              className="px-6 py-3 bg-gradient-to-r from-[#6C4FFF] to-[#5840D9] text-white rounded-lg hover:opacity-90 transition font-medium"
              onClick={() => mine()}
            >
              Mine
            </button>
            {foundHash && (
              <button
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:opacity-90 transition font-medium"
                onClick={() => {
                  save(
                    calculatedHash,
                    timestamp,
                    previousBlockHash,
                    transactions,
                    nonce,
                  );
                  toast.success("Saved to BlockChain");
                }}
              >
                Save to BlockChain
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
